import { fire } from "./analytics";

// Fixed, low-cardinality measurements only: never URLs, account IDs or bodies.
// 5% of page sessions; consent/config gates in fire() remain authoritative.
const sampled = Math.random() < 0.05;
const totals = { requests: 0, failed: 0, aborted: 0, requestMs: 0, longTasks: 0, longTaskMs: 0, maxInteractionMs: 0, lcpMs: 0, transferBytes: 0, cacheHits: 0, cacheMisses: 0 };
export function performanceSnapshot() { return { ...totals }; }
export function recordRequest(duration: number, outcome: "ok" | "failed" | "aborted") {
  totals.requests++; totals.requestMs += Math.round(duration);
  if (outcome !== "ok") totals[outcome]++;
}
export function recordCache(hit: boolean) { totals[hit ? "cacheHits" : "cacheMisses"]++; }
export function startPerformanceMonitoring(): () => void {
  if (typeof PerformanceObserver === "undefined") return () => {};
  const observers: PerformanceObserver[] = [];
  for (const type of ["longtask", "event", "largest-contentful-paint", "resource"]) {
    if (!PerformanceObserver.supportedEntryTypes.includes(type)) continue;
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (type === "longtask") { totals.longTasks++; totals.longTaskMs += Math.round(entry.duration); }
        if (type === "event") totals.maxInteractionMs = Math.max(totals.maxInteractionMs, Math.round(entry.duration));
        if (type === "largest-contentful-paint") totals.lcpMs = Math.round(entry.startTime);
        if (type === "resource") totals.transferBytes += (entry as PerformanceResourceTiming).transferSize || 0;
      }
    });
    try {
      observer.observe({ type, buffered: true });
      observers.push(observer);
    } catch { observer.disconnect(); /* optional diagnostics cannot break the app */ }
  }
  // Send bounded interval deltas while the page is alive. Async analytics on
  // pagehide is not reliable; short sessions may deliberately produce no event.
  let previous = { ...totals };
  let lastFlush = Date.now();
  const flush = () => {
    if (!sampled || document.hidden || Date.now() - lastFlush < 30_000) return;
    const current = { ...totals };
    const delta = Object.fromEntries(Object.entries(current).map(([key, value]) => [
      key, key === "lcpMs" || key === "maxInteractionMs"
        ? value : value - previous[key as keyof typeof totals]
    ]));
    previous = current;
    const intervalMs = Date.now() - lastFlush;
    lastFlush = Date.now();
    void fire("app_performance", { ...delta, sample_denominator: 20, interval_ms: intervalMs });
  };
  const timer = window.setInterval(flush, 30_000);
  document.addEventListener("visibilitychange", flush);
  return () => {
    observers.forEach(observer => observer.disconnect());
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", flush);
  };
}
